import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useDispatch, useSelector, useStore } from "react-redux";
import { auth } from "../../firebase";
import { ACCESS_CLEARED, ACCESS_FAILED, ACCESS_RECEIVED, ACCESS_REQUESTED } from "../../redux/reducers/accessReducer";
import { talentLenzOpportunityAccessApi } from "../../talentLenz/talentLenzOpportunityAccessApi";
import { platformAccessApi } from "../api/platformAccessApi";
import { accessIdentityKey, accessRetryAt, accessSnapshotKey, isTransientAccessError, serializableAccessError } from "./accessCachePolicy";
import { ACCESS_CHANGED_EVENT } from "./accessCacheEvents";

export { routeForAccessContext, routeForUnityRoles, routeAfterCentralLogin, APPLICANT_APPLICATION_ROUTE, APPLICANT_WORKSPACE_ROUTE } from "./accessRouting";

const AccessContext = createContext(null);
const AccessDataContext = createContext(null);
const AccessActionsContext = createContext(null);
const AccessRefreshContext = createContext(null);
const ACCESS_TOKEN_TIMEOUT_MS = 10000;
const EMPTY_TENANTS = Object.freeze([]);

function accessTokenWithDeadline(currentUser, forceTokenRefresh) {
  let timeout;
  const deadline = new Promise((_, reject) => {
    timeout = setTimeout(() => {
      const error = new Error("The secure session refresh took too long.");
      error.code = "functions/deadline-exceeded";
      reject(error);
    }, ACCESS_TOKEN_TIMEOUT_MS);
  });

  return Promise.race([
    currentUser.getIdTokenResult(forceTokenRefresh),
    deadline,
  ]).finally(() => clearTimeout(timeout));
}

export function AccessProvider({ children }) {
  const dispatch = useDispatch();
  const store = useStore();
  const cache = useSelector((state) => state.access);
  const [user, setUser] = useState(auth.currentUser);
  const [accessClaims, setAccessClaims] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const refreshSequence = useRef(0);
  const pendingRefresh = useRef(null);
  const pendingTenantSwitch = useRef(null);
  const tenantWrites = useRef(Promise.resolve());
  const mounted = useRef(true);

  const updateAccessClaims = useCallback((claims) => {
    setAccessClaims((previous) => accessSnapshotKey(previous) === accessSnapshotKey(claims)
      ? previous : claims);
  }, []);

  // A pending automatic claim synchronization must finish before a subsequent
  // explicit switch writes another tenant, or it could undo that selection.
  const writeActiveTenant = useCallback((tenantId, isCurrent) => {
    const request = tenantWrites.current.catch(() => {}).then(() => (
      isCurrent() ? platformAccessApi.setActiveTenant(tenantId) : null
    ));
    tenantWrites.current = request;
    return request;
  }, []);

  const clearAccess = useCallback(() => {
    refreshSequence.current += 1;
    pendingRefresh.current = null;
    pendingTenantSwitch.current = null;
    setAccessClaims(null);
    dispatch({ type: ACCESS_CLEARED });
  }, [dispatch]);

  // Explicit callers always fetch. Only automatic focus/expiry checks may use
  // the cache. Firebase continues to own the user object and all tokens.
  const refreshAccessContext = useCallback(async ({ forceTokenRefresh = false, authUser, background = false, preferCache = false } = {}) => {
    const currentUser = authUser || auth.currentUser;
    if (!currentUser) {
      clearAccess();
      setUser(null);
      setInitializing(false);
      return null;
    }
    if (!mounted.current || auth.currentUser !== currentUser) return null;
    if (pendingTenantSwitch.current) {
      return pendingTenantSwitch.current.promise.then((result) => result.accessContext);
    }
    const identityKey = accessIdentityKey(currentUser);
    setUser(currentUser);
    const pending = pendingRefresh.current;
    if (pending?.user === currentUser && pending.identityKey === identityKey &&
        (!forceTokenRefresh || pending.forceTokenRefresh)) return pending.promise;

    const previous = store.getState().access;
    const sameIdentity = previous.identityKey === identityKey;
    if (preferCache && !forceTokenRefresh && sameIdentity && previous.status === "ready" &&
        (previous.expiresAt > Date.now() || previous.retryAt > Date.now())) return previous.context;
    if (!sameIdentity) setAccessClaims(null);
    const sequence = ++refreshSequence.current;
    const isCurrent = () => mounted.current && sequence === refreshSequence.current &&
      auth.currentUser === currentUser && accessIdentityKey(currentUser) === identityKey;
    dispatch({ type: ACCESS_REQUESTED, payload: {
      uid: currentUser.uid, identityKey, requestId: sequence, background,
    } });

    const request = (async () => {
      try {
        let tokenResult = await accessTokenWithDeadline(currentUser, forceTokenRefresh);
        if (!isCurrent()) return null;
        updateAccessClaims(tokenResult.claims || {});
        const nextContext = (currentUser.emailVerified ?
          await platformAccessApi.getAccessContext() :
          await talentLenzOpportunityAccessApi.getAccessContext()) || null;
        if (!isCurrent()) return null;
        const tenant = nextContext?.activeTenant;
        if (currentUser.emailVerified && tenant &&
            (tokenResult.claims?.tenantId !== tenant.tenantId ||
             tokenResult.claims?.tenantRole !== tenant.tenantRole)) {
          await writeActiveTenant(tenant.tenantId, isCurrent);
          if (!isCurrent()) return null;
          tokenResult = await accessTokenWithDeadline(currentUser, true);
        } else if (!forceTokenRefresh && sameIdentity && previous.context &&
            accessSnapshotKey(previous.context) !== accessSnapshotKey(nextContext)) {
          // Refresh changed claims without fetching the same context a second time.
          tokenResult = await accessTokenWithDeadline(currentUser, true);
        }
        if (!isCurrent()) return null;
        updateAccessClaims(tokenResult.claims || {});
        dispatch({ type: ACCESS_RECEIVED, payload: {
          identityKey, requestId: sequence, context: nextContext, now: Date.now(),
        } });
        return store.getState().access.context;
      } catch (nextError) {
        if (!isCurrent()) return null;
        dispatch({ type: ACCESS_FAILED, payload: {
          identityKey,
          requestId: sequence,
          error: serializableAccessError(nextError),
          keepContext: background && isTransientAccessError(nextError),
          retryAt: accessRetryAt(nextError, (sameIdentity ? previous.failures : 0) + 1, Date.now()),
        } });
        throw nextError;
      } finally {
        if (isCurrent()) {
          pendingRefresh.current = null;
          setInitializing(false);
        }
      }
    })();
    pendingRefresh.current = { user: currentUser, identityKey, forceTokenRefresh, promise: request };
    return request;
  }, [clearAccess, dispatch, store, updateAccessClaims, writeActiveTenant]);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    let observedUser;
    let observedIdentity;
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!active || !mounted.current) return;
      const nextIdentity = accessIdentityKey(nextUser);
      if (observedUser !== nextUser || observedIdentity !== nextIdentity) {
        clearAccess();
        setInitializing(Boolean(nextUser));
      }
      observedUser = nextUser;
      observedIdentity = nextIdentity;
      setUser(nextUser);
      if (!nextUser) {
        setInitializing(false);
        return;
      }

      try {
        await refreshAccessContext({ authUser: nextUser, preferCache: true });
      } catch {
        // Initial access verification fails closed; route components present it.
      }
    });
    return () => {
      active = false;
      mounted.current = false;
      refreshSequence.current += 1;
      pendingRefresh.current = null;
      pendingTenantSwitch.current = null;
      // The central boundary is not mounted on legacy/public routes. Leaving it
      // must not leave a previous account's snapshot in the shared Redux store.
      dispatch({ type: ACCESS_CLEARED });
      unsubscribe();
    };
  }, [clearAccess, dispatch, refreshAccessContext]);

  useEffect(() => {
    const accessChanged = (event) => {
      if (event.detail?.uid !== auth.currentUser?.uid || pendingTenantSwitch.current) return;
      const current = store.getState().access;
      if (current.identityKey !== accessIdentityKey(auth.currentUser)) return;
      const tenantId = event.detail?.tenantId;
      if (tenantId && current.context?.activeTenant?.tenantId !== tenantId &&
          !current.context?.availableTenants?.some((tenant) => tenant.tenantId === tenantId)) return;
      // Do not join a read that began before the access mutation completed.
      refreshSequence.current += 1;
      pendingRefresh.current = null;
      refreshAccessContext({ forceTokenRefresh: true, background: true }).catch(() => {});
    };
    window.addEventListener(ACCESS_CHANGED_EVENT, accessChanged);
    return () => window.removeEventListener(ACCESS_CHANGED_EVENT, accessChanged);
  }, [refreshAccessContext, store]);

  // Schedule a single refresh at expiry. Focus/visibility events do no network
  // work while fresh, and hidden tabs wait until they become visible again.
  useEffect(() => {
    if (cache.identityKey !== accessIdentityKey(user) || cache.status !== "ready" ||
        cache.refreshing || initializing) return undefined;
    let active = true;
    let timer;
    const checkAccess = async () => {
      clearTimeout(timer);
      if (!active || document.visibilityState === "hidden") return;
      const current = store.getState().access;
      if (current.identityKey !== accessIdentityKey(user)) return;
      const dueAt = Math.max(current.expiresAt || 0, current.retryAt);
      if (dueAt > Date.now()) {
        timer = setTimeout(checkAccess, Math.min(2147483647, dueAt - Date.now()));
        return;
      }
      try {
        await refreshAccessContext({ background: true, preferCache: true });
      } catch {
        // Transient errors retain the display cache and schedule a backed-off
        // retry. Permission/session errors clear access and stop automatic work.
      }
    };
    checkAccess();
    window.addEventListener("focus", checkAccess);
    document.addEventListener("visibilitychange", checkAccess);
    return () => {
      active = false;
      clearTimeout(timer);
      window.removeEventListener("focus", checkAccess);
      document.removeEventListener("visibilitychange", checkAccess);
    };
  }, [user, cache, initializing, refreshAccessContext, store]);

  const selectTenant = useCallback(
    (tenantId) => {
      if (pendingTenantSwitch.current) return pendingTenantSwitch.current.promise;
      const currentUser = auth.currentUser;
      if (!currentUser) return Promise.reject(Object.assign(new Error("Sign in before switching company."), { code: "functions/unauthenticated" }));
      const identityKey = accessIdentityKey(currentUser);
      clearAccess();
      setInitializing(true);
      const sequence = refreshSequence.current;
      const isCurrent = () => mounted.current && sequence === refreshSequence.current &&
        auth.currentUser === currentUser && accessIdentityKey(currentUser) === identityKey;
      dispatch({ type: ACCESS_REQUESTED, payload: {
        uid: currentUser?.uid || null, identityKey, requestId: sequence,
      } });
      const operation = { promise: null };
      const request = (async () => {
        try {
          const result = await writeActiveTenant(tenantId, isCurrent);
          if (!isCurrent()) {
            return { ...result, accessContext: null };
          }
          pendingTenantSwitch.current = null;
          const accessContext = await refreshAccessContext({
            forceTokenRefresh: result?.requiresTokenRefresh !== false,
          });
          return { ...result, accessContext };
        } catch (nextError) {
          if (isCurrent()) {
            dispatch({ type: ACCESS_FAILED, payload: {
              identityKey, requestId: sequence, error: serializableAccessError(nextError), retryAt: 0,
            } });
            setInitializing(false);
          }
          throw nextError;
        } finally {
          if (pendingTenantSwitch.current === operation) pendingTenantSwitch.current = null;
        }
      })();
      operation.promise = request;
      pendingTenantSwitch.current = operation;
      return request;
    },
    [clearAccess, dispatch, refreshAccessContext, writeActiveTenant],
  );

  const signOutUser = useCallback(async () => {
    clearAccess();
    setUser(null);
    setInitializing(false);
    await signOut(auth);
  }, [clearAccess]);

  const ownsCache = cache.identityKey === accessIdentityKey(user);
  const accessContext = ownsCache ? cache.context : null;
  const error = ownsCache ? cache.error : null;
  const loading = initializing || Boolean(user && (!ownsCache || cache.status === "idle" || cache.status === "loading"));

  const data = useMemo(
    () => ({
      user,
      accessContext,
      platform: accessContext?.platform || null,
      activeTenant: accessContext?.activeTenant || null,
      availableTenants: accessContext?.availableTenants || EMPTY_TENANTS,
      loading,
      error,
    }),
    [user, accessContext, loading, error],
  );
  const actions = useMemo(
    () => ({ refreshAccessContext, selectTenant, signOutUser }),
    [refreshAccessContext, selectTenant, signOutUser],
  );
  const refreshing = ownsCache && cache.refreshing;
  const refreshError = ownsCache ? cache.refreshError : null;
  const retryAt = ownsCache ? cache.retryAt : 0;
  const refreshState = useMemo(
    () => ({ refreshing, refreshError, retryAt }),
    [refreshing, refreshError, retryAt],
  );
  // Keep the existing flat API, including claims, for legacy consumers. Focused
  // consumers subscribe only to the contexts they need.
  const value = useMemo(
    () => ({ ...data, accessClaims, ...refreshState, ...actions }),
    [data, accessClaims, refreshState, actions],
  );

  return (
    <AccessContext.Provider value={value}>
      <AccessDataContext.Provider value={data}>
        <AccessActionsContext.Provider value={actions}>
          <AccessRefreshContext.Provider value={refreshState}>
            {children}
          </AccessRefreshContext.Provider>
        </AccessActionsContext.Provider>
      </AccessDataContext.Provider>
    </AccessContext.Provider>
  );
}

function useRequiredAccessContext(context, hookName) {
  const value = useContext(context);
  if (!value) throw new Error(`${hookName} must be used inside AccessProvider.`);
  return value;
}

export function useAccess() {
  return useRequiredAccessContext(AccessContext, "useAccess");
}

export function useAccessData() {
  return useRequiredAccessContext(AccessDataContext, "useAccessData");
}

export function useAccessActions() {
  return useRequiredAccessContext(AccessActionsContext, "useAccessActions");
}

export function useAccessRefreshState() {
  return useRequiredAccessContext(AccessRefreshContext, "useAccessRefreshState");
}
